import React, {useEffect, useState} from 'react';
import {Box, Button, Chip, Paper, Typography, IconButton} from '@mui/material';
import {Collections as GalleryIcon, Clear as ClearIcon, Add as AddIcon} from '@mui/icons-material';
import Gallery from './Gallery';

/**
 * GalleryPicker - A simple component for file selection with gallery
 *
 * @param {Object} props
 * @param {Array} props.value - Selected files array
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Array} props.availableFiles - List of available files to select from
 * @param {Function} props.onFileUpload - Callback when new files are uploaded
 * @param {Function} props.onFileDelete - Callback when files are deleted
 * @param {boolean} props.allowMultiple - Allow multiple file selection
 * @param {number} props.maxFiles - Maximum number of files
 * @param {number} props.maxSizeInMB - Maximum file size in MB
 * @param {Array} props.fileTypes - Allowed file types
 * @param {string} props.label - Label for the field
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disable the component
 * @param {boolean} props.showPreview - Show file previews
 * @param {Object} props.previewStyle - Custom preview styles
 * @param {boolean} props.showDeleteButton - Show delete buttons in gallery
 * @param {boolean} props.enableMainImage - Enable main image selection
 * @param {Function} props.onMainImageChange - Callback when main image changes
 * @param {Object} props.mainImage - Current main image
 */
const GalleryPicker = ({
	value = [],
	onChange,
	defaultAvailableFiles = [],
	onFileUpload,
	allowMultiple = false,
	maxFiles = 10,
	maxSizeInMB = 5,
	fileTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
	label,
	placeholder = allowMultiple ? 'اختر الملفات' : 'اختر ملف',
	disabled = false,
	showPreview = true,
	previewStyle = {},
	error,
	helperText,
	showDeleteButton = false,
	// Cropping options
	enableCrop = false,
	forceCrop = false,
	readOnly = false,
	cropRatio = 4 / 5,
	// Main image selection
	enableMainImage = false,
	onMainImageChange,
	mainImage = null,
}) => {
	const [galleryOpen, setGalleryOpen] = useState(false);

	// Ensure value is always an array for consistent handling
	const selectedFiles = allowMultiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];
	const [availableFiles, setAvailableFiles] = useState(
		getAvailableFiles({
			selectedFiles,
			availableFiles: defaultAvailableFiles,
		}),
	);

	// Handle file uploads with auto-selection
	const handleFileUpload = (uploadedFiles) => {
		// Call the parent's upload handler first
		if (onFileUpload) {
			onFileUpload(uploadedFiles);
		}

		// Auto-select the uploaded files
		if (allowMultiple) {
			// For multiple selection, add new files to existing selection (respecting maxFiles limit)
			const currentSelection = selectedFiles;
			const availableSlots = maxFiles - currentSelection.length;
			const filesToAdd = uploadedFiles.slice(0, availableSlots);
			const newSelection = [...currentSelection, ...filesToAdd];
			onChange(newSelection);
		} else {
			// For single selection, select the first uploaded file
			onChange(uploadedFiles[0] || null);
		}

		setAvailableFiles((prev) => [...prev, ...uploadedFiles]);

		// Close the gallery after successful upload
		// setGalleryOpen(false);
	};

	const handleGallerySelect = (files) => {
		if (allowMultiple) {
			onChange(files);
		} else {
			onChange(files);
		}
		setGalleryOpen(false);
	};

	const handleFileRemove = (fileToRemove) => {
		if (allowMultiple) {
			const newFiles = selectedFiles.filter((file) => file.id !== fileToRemove.id);
			onChange(newFiles);
		} else {
			onChange(null);
		}
	};

	const handleFileDelete = async (file) => {
		console.log('Deleting file:', file);
		// Remove file from available files
		setAvailableFiles((prev) => prev.filter((f) => f.id !== file.id));
		// Also remove from selected files if it was selected
		if (!allowMultiple) {
			onChange(null);
		} else {
			onChange(selectedFiles.filter((f) => f.id !== file.id));
		}
	};

	const handleFilesClear = () => {
		onChange(allowMultiple ? [] : null);
	};

	// Handle opening gallery with auto file selection for empty state
	const handleOpenGallery = () => {
		if (disabled) return;

		// If no available files, we'll let the Gallery component handle auto-opening file dialog
		setGalleryOpen(true);
	};

	return (
		<Box>
			{/* Label */}
			{label && (
				<Typography variant="subtitle2" gutterBottom>
					{label}
				</Typography>
			)}

			{/* File selection area */}
			<Paper
				variant="outlined"
				sx={{
					p: 2,
					borderColor: error ? 'error.main' : 'divider',
					borderStyle: error ? 'solid' : 'dashed',
					borderWidth: error ? 2 : 1,
					textAlign: 'center',
					cursor: disabled ? 'not-allowed' : 'pointer',
					opacity: disabled ? 0.6 : 1,
					'&:hover': {
						borderColor: disabled ? 'divider' : 'primary.main',
						bgcolor: disabled ? 'transparent' : 'grey.50',
					},
				}}
				onClick={handleOpenGallery}
			>
				{selectedFiles.length === 0 ? (
					<Box py={2}>
						<GalleryIcon sx={{fontSize: 48, color: 'grey.400', mb: 1}} />
						<Typography variant="body1" color="grey.600">
							{placeholder}
						</Typography>
						<Typography variant="caption" color="grey.500">
							انقر لفتح معرض الملفات
						</Typography>
					</Box>
				) : (
					<Box>
						<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
							<Typography variant="body2" color="grey.700">
								تم اختيار {selectedFiles.length} {allowMultiple ? 'ملفات' : 'ملف'}
							</Typography>
							{!readOnly && (
								<Box>
									<Button
										size="small"
										startIcon={<AddIcon />}
										onClick={(e) => {
											e.stopPropagation();
											handleOpenGallery();
										}}
										disabled={disabled}
									>
										{allowMultiple ? 'إضافة المزيد' : 'تغيير'}
									</Button>
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											handleFilesClear();
										}}
										disabled={disabled}
									>
										<ClearIcon />
									</IconButton>
								</Box>
							)}
						</Box>

						{/* File previews */}
						{showPreview && (
							<Box>
								{allowMultiple ? (
									<Box
										sx={{
											display: 'flex',
											gap: 1,
											overflowX: 'auto',
											overflowY: 'visible',
											pb: enableMainImage ? 3 : 1,
											'&::-webkit-scrollbar': {
												height: 6,
											},
											'&::-webkit-scrollbar-track': {
												backgroundColor: 'grey.100',
												borderRadius: 3,
											},
											'&::-webkit-scrollbar-thumb': {
												backgroundColor: 'grey.400',
												borderRadius: 3,
												'&:hover': {
													backgroundColor: 'grey.600',
												},
											},
											// Mobile optimization
											scrollSnapType: 'x mandatory',
											WebkitOverflowScrolling: 'touch',
										}}
									>
										{selectedFiles.map((file) => {
											const isMainImage = enableMainImage && mainImage?.id === file.id;
											return (
												<Box
													key={file.id}
													sx={{
														position: 'relative',
														flexShrink: 0,
														width: 80,
														height: 80,
														borderRadius: 1,
														overflow: 'visible',
														scrollSnapAlign: 'start',
														mb: enableMainImage ? 2.5 : 0,
														'&:hover .remove-button': {
															opacity: 1,
														},
													}}
												>
													<Box
														sx={{
															width: '100%',
															height: 80,
															borderRadius: 1,
															overflow: 'hidden',
															border: isMainImage ? '2px solid' : '1px solid',
															borderColor: isMainImage ? 'primary.main' : 'grey.300',
															boxShadow: isMainImage ? 2 : 0,
														}}
													>
														{file.mime?.startsWith('image/') ? (
															<img
																src={file.url}
																alt={file.name}
																style={{
																	width: '100%',
																	height: '100%',
																	objectFit: 'cover',
																}}
															/>
														) : (
															<Box
																sx={{
																	width: '100%',
																	height: '100%',
																	display: 'flex',
																	flexDirection: 'column',
																	alignItems: 'center',
																	justifyContent: 'center',
																	bgcolor: 'grey.100',
																}}
															>
																<Typography variant="h6" sx={{fontSize: 24}}>
																	📄
																</Typography>
																<Typography
																	variant="caption"
																	sx={{
																		fontSize: 10,
																		textAlign: 'center',
																		px: 0.5,
																		overflow: 'hidden',
																		textOverflow: 'ellipsis',
																		whiteSpace: 'nowrap',
																		width: '100%',
																	}}
																>
																	{file.name}
																</Typography>
															</Box>
														)}
													</Box>

													{/* Main image selection button - always visible */}
													{enableMainImage && !readOnly && (
														<Button
															size="small"
															variant={isMainImage ? 'contained' : 'outlined'}
															color="primary"
															onClick={(e) => {
																e.stopPropagation();
																if (!isMainImage && onMainImageChange) {
																	onMainImageChange(file);
																}
															}}
															disabled={disabled || isMainImage}
															sx={{
																position: 'absolute',
																bottom: -20,
																left: 0,
																right: 0,
																height: 18,
																minHeight: 18,
																fontSize: '0.65rem',
																px: 0.5,
																py: 0,
																borderRadius: 0.5,
																textTransform: 'none',
																'& .MuiButton-startIcon': {
																	marginRight: 0.25,
																	marginLeft: 0,
																},
															}}
															startIcon={
																isMainImage ? (
																	<Typography sx={{fontSize: 12}}>✓</Typography>
																) : null
															}
														>
															{isMainImage ? 'رئيسية' : 'تعيين كرئيسية'}
														</Button>
													)}

													{/* Remove button */}
													<IconButton
														className="remove-button"
														size="small"
														onClick={(e) => {
															e.stopPropagation();
															handleFileRemove(file);
														}}
														disabled={disabled}
														sx={{
															position: 'absolute',
															top: 2,
															right: 2,
															bgcolor: 'rgba(0,0,0,0.6)',
															color: 'white',
															opacity: 0,
															transition: 'opacity 0.2s',
															width: 20,
															height: 20,
															'&:hover': {
																bgcolor: 'rgba(0,0,0,0.8)',
															},
															'@media (max-width: 600px)': {
																opacity: 1, // Always visible on mobile
															},
														}}
													>
														<ClearIcon sx={{fontSize: 12}} />
													</IconButton>
												</Box>
											);
										})}
									</Box>
								) : (
									selectedFiles[0] && (
										<Box
											sx={{
												position: 'relative',
												display: 'inline-block',
												borderRadius: 1,
												overflow: 'hidden',
												border: '1px solid',
												borderColor: 'grey.300',
												'&:hover .remove-button': {
													opacity: 1,
												},
											}}
										>
											{selectedFiles[0].mime?.startsWith('image/') ? (
												<img
													src={selectedFiles[0].url}
													alt={selectedFiles[0].name}
													style={{
														width: 120,
														height: 120,
														objectFit: 'cover',
														display: 'block',
														...previewStyle,
													}}
												/>
											) : (
												<Box
													sx={{
														width: 120,
														height: 120,
														display: 'flex',
														flexDirection: 'column',
														alignItems: 'center',
														justifyContent: 'center',
														bgcolor: 'grey.100',
													}}
												>
													<Typography variant="h4" sx={{fontSize: 32, mb: 1}}>
														📄
													</Typography>
													<Typography
														variant="caption"
														sx={{
															textAlign: 'center',
															px: 1,
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap',
															width: '100%',
														}}
													>
														{selectedFiles[0].name}
													</Typography>
												</Box>
											)}

											{/* Remove button */}
											<IconButton
												className="remove-button"
												size="small"
												onClick={(e) => {
													e.stopPropagation();
													handleFileRemove(selectedFiles[0]);
												}}
												disabled={disabled}
												sx={{
													position: 'absolute',
													top: 4,
													right: 4,
													bgcolor: 'rgba(0,0,0,0.6)',
													color: 'white',
													opacity: 0,
													transition: 'opacity 0.2s',
													width: 24,
													height: 24,
													'&:hover': {
														bgcolor: 'rgba(0,0,0,0.8)',
													},
													'@media (max-width: 600px)': {
														opacity: 1, // Always visible on mobile
													},
												}}
											>
												<ClearIcon sx={{fontSize: 14}} />
											</IconButton>
										</Box>
									)
								)}
							</Box>
						)}
					</Box>
				)}
			</Paper>

			{/* Error message */}
			{error && helperText && (
				<Typography variant="caption" color="error" display="block" mt={0.5}>
					{helperText}
				</Typography>
			)}

			{/* Gallery Dialog */}
			<Gallery
				open={galleryOpen}
				onClose={() => setGalleryOpen(false)}
				onSelect={handleGallerySelect}
				allowMultiple={allowMultiple}
				selectedFiles={selectedFiles}
				availableFiles={availableFiles}
				onFileUpload={handleFileUpload}
				onFileDelete={handleFileDelete}
				maxFiles={maxFiles}
				maxSizeInMB={maxSizeInMB}
				fileTypes={fileTypes}
				showDeleteButton={showDeleteButton}
				enableCrop={enableCrop}
				forceCrop={forceCrop}
				cropRatio={cropRatio}
				readOnly={readOnly}
				onChange={onChange}
			/>
		</Box>
	);
};

function getAvailableFiles({selectedFiles, availableFiles}) {
	// return available files with the selected files that not already exist in available files
	const missingSelectedFiles = selectedFiles.filter(
		(file) => !availableFiles.find((f) => f.id === file.id),
	);
	return [...availableFiles, ...missingSelectedFiles];
}

export default GalleryPicker;
